import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Ban, ShoppingCart, XCircle } from "lucide-react";
import { toast } from "sonner";
import usePoll from "@/hooks/usePoll";
import { useAuth } from "@/context/AuthContext";
import { api, dt, errMsg, money, SEL_LABEL } from "@/lib/api";
import { Empty, ErrorBox, ExportBar, Loading, Modal, PageHeader, ReasonDialog, Sel } from "@/components/common";
import { SelectionBadge, SelectionInfo } from "@/components/Selection";

function Filters({ f, setF, workers, sections }) {
  const set = (k) => (v) => setF({ ...f, [k]: typeof v === "string" ? v : v.target.value });
  return (
    <div className="k-card p-4 grid grid-cols-2 md:grid-cols-6 gap-3 no-print">
      <input className="k-input num col-span-2 md:col-span-1" placeholder="رقم الكبش" value={f.q} onChange={set("q")} data-testid="sel-filter-tag" />
      <Sel value={f.status} onChange={set("status")} testid="sel-filter-status"><option value="">كل الحالات</option>{Object.entries(SEL_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</Sel>
      <Sel value={f.worker_id} onChange={set("worker_id")} testid="sel-filter-worker"><option value="">كل العمال</option>{workers.map((w) => <option key={w.id} value={w.id}>{w.full_name}</option>)}</Sel>
      <Sel value={f.section_id} onChange={set("section_id")} testid="sel-filter-section"><option value="">كل الأقسام</option>{sections.map((s) => <option key={s.id} value={s.id}>{s.code} – {s.name}</option>)}</Sel>
      <input type="date" className="k-input" value={f.date_from} onChange={set("date_from")} data-testid="sel-filter-from" />
      <input type="date" className="k-input" value={f.date_to} onChange={set("date_to")} data-testid="sel-filter-to" />
    </div>
  );
}

function Actions({ sel, onDone }) {
  const { can } = useAuth();
  const nav = useNavigate();
  const [dlg, setDlg] = useState(null);
  if (sel.status !== "pending") return sel.sale_id ? <button className="k-btn-outline" onClick={() => nav(`/sales/${sel.sale_id}`)} data-testid="open-sale-btn">فتح عملية البيع {sel.sale_number}</button> : null;
  const change = async (status, reason) => {
    try { await api.post(`/selections/${sel.id}/status`, { status, reason }); toast.success("تم تحديث حالة السجل"); onDone(); } catch (e) { throw new Error(errMsg(e)); }
  };
  return (
    <div className="flex flex-wrap gap-2">
      {can("create_sale") && <button className="k-btn-primary" onClick={() => nav(`/sales/new?selection=${sel.id}`)} data-testid="complete-sale-btn"><ShoppingCart className="w-5 h-5" /> استكمال بيانات الزبون وإتمام البيع</button>}
      <button className="k-btn-outline text-rose-700" onClick={() => setDlg("failed")} data-testid="mark-failed-btn"><XCircle className="w-4 h-4" /> تعذر إتمام البيع</button>
      {can("manage_selections") && <button className="k-btn-outline text-slate-600" onClick={() => setDlg("cancelled")} data-testid="cancel-selection-btn"><Ban className="w-4 h-4" /> إلغاء</button>}
      <ReasonDialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)} testid="selection-status-dialog"
        title={dlg === "failed" ? "تعذر إتمام البيع" : "إلغاء سجل الاختيار"} onConfirm={(r) => change(dlg, r)} />
    </div>
  );
}

export default function Selections() {
  const [sp] = useSearchParams();
  const [f, setF] = useState({ q: sp.get("q") || "", status: sp.get("q") ? "" : "pending", worker_id: "", section_id: "", date_from: "", date_to: "" });
  const [workers, setWorkers] = useState([]);
  const [sections, setSections] = useState([]);
  const [open, setOpen] = useState(null);
  const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
  const { data, error, reload } = usePoll("/selections", params, 15000);
  useEffect(() => {
    api.get("/users/workers").then((r) => setWorkers(r.data)).catch(() => {});
    api.get("/sections").then((r) => setSections(r.data)).catch(() => {});
  }, []);
  const cols = [{ key: "number", label: "السجل" }, { key: "ear_tag", label: "رقم الكبش" }, { key: "section_name", label: "القسم" }, { key: "price", label: "السعر" },
    { key: "worker_name", label: "العامل" }, { get: (r) => dt(r.selected_at), label: "تاريخ الاختيار" }, { key: "status_label", label: "الحالة" },
    { key: "sale_number", label: "عملية البيع" }, { key: "clerk_name", label: "موظف المكتب" }];
  return (
    <div className="space-y-4">
      <PageHeader title="اختيارات الحضيرة" subtitle="كباش سجلها العمال من الهاتف – افتح السجل لاستكمال بيانات الزبون وتأكيد البيع"><ExportBar name="selections" cols={cols} rows={data} /></PageHeader>
      <Filters f={f} setF={setF} workers={workers} sections={sections} />
      <ErrorBox text={error} />
      {!data ? <Loading /> : !data.length ? <div className="k-card"><Empty text="لا توجد سجلات مطابقة" /></div> : (
        <div className="k-card overflow-x-auto"><table className="k-table" data-testid="selections-table">
          <thead><tr><th>رقم الكبش</th><th>القسم</th><th>السعر</th><th>العامل</th><th>تاريخ الاختيار</th><th>صور</th><th>الحالة</th></tr></thead>
          <tbody>{data.map((s) => (
            <tr key={s.id} className="cursor-pointer hover:bg-[#F6F1E7]" onClick={() => setOpen(s)} data-testid={`selection-row-${s.ear_tag}`}>
              <td className="num font-black text-lg">{s.ear_tag}</td><td>{s.section_name}</td><td className="num">{money(s.price)}</td><td>{s.worker_name}</td>
              <td className="text-xs whitespace-nowrap">{dt(s.selected_at)}</td><td className="num">{s.photos.length}</td><td><SelectionBadge s={s.status} /></td>
            </tr>))}</tbody>
        </table></div>
      )}
      <Modal open={!!open} onOpenChange={(o) => !o && setOpen(null)} wide title={`سجل اختيار الكبش ${open?.ear_tag}`}
        footer={open && <Actions sel={open} onDone={() => { setOpen(null); reload(); }} />}>
        {open && <SelectionInfo sel={open} />}
      </Modal>
    </div>
  );
}
